import type { InventoryPage, InventoryRow, ListInventoryParams } from '@/infrastructure/FulfillmentAdminClient';
import type { ProductLookupResponse } from '@/infrastructure/StockClient';

/** The server's page size; the screen reads page after page rather than stopping at it [SSD-7529]. */
export const INVENTORY_PAGE_SIZE = 500;

/** A loop the server never ends is a bug, not a warehouse: 200 pages is 100,000 rows. */
const MAX_PAGES = 200;

/** Skus per request: they ride in the address, and a whole warehouse of them would outgrow it. */
export const SKU_CHUNK = 150;

/** Catalog lookups running at once. */
const CATALOG_LOOKUP_PARALLEL = 4;

type ListInventory = (params: ListInventoryParams) => Promise<InventoryPage>;

/**
 * Every row the read names, page after page, in the server's (location, sku) order. The server stops a page at
 * 500 rows, and a screen that stopped there too left every later shelf off — on prod, 24 stocked shelves after C9
 * once zero rows were asked for. `stillWanted` is asked after every page, so a read a newer one has replaced stops
 * at once and answers null. A server that predates paging hands out no cursor, which reads as one page, as before.
 */
export const readEveryInventoryPage = async (
  list: ListInventory,
  params: Omit<ListInventoryParams, 'cursor' | 'limit'>,
  stillWanted: () => boolean = () => true,
): Promise<InventoryRow[] | null> => {
  const rows: InventoryRow[] = [];
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result: InventoryPage = await list({ ...params, limit: INVENTORY_PAGE_SIZE, ...(cursor ? { cursor } : {}) });
    if (!stillWanted()) {
      return null;
    }
    rows.push(...result.rows);
    cursor = result.nextCursor ?? null;
    if (!cursor) {
      return rows;
    }
  }
  throw new Error('The stock list did not end.');
};

/**
 * The walk's own rows the read left out because they sit at zero. A target that falls to zero mid-walk must stay
 * on screen to be counted, and asking for every zero row in the warehouse to keep a handful costs thousands of rows
 * (prod: 4,266 at WH#00 alone), so only the missing targets' skus are read again with their zero rows [SSD-7529].
 */
export const readMissingTargets = async (
  list: ListInventory,
  loaded: InventoryRow[],
  targetsByShelf: Record<string, string[]>,
  stillWanted: () => boolean = () => true,
): Promise<InventoryRow[] | null> => {
  const have = new Set(loaded.map((row) => `${row.sku}@${row.locationCode}`));
  const missing = new Set(
    Object.entries(targetsByShelf).flatMap(([locationCode, skus]) =>
      skus.map((sku) => `${sku}@${locationCode}`).filter((key) => !have.has(key)),
    ),
  );
  if (missing.size === 0) {
    return [];
  }
  const skus = [...new Set([...missing].map((key) => key.slice(0, key.lastIndexOf('@'))))];
  const found: InventoryRow[] = [];
  for (let start = 0; start < skus.length; start += SKU_CHUNK) {
    const page = await readEveryInventoryPage(
      list,
      { skus: skus.slice(start, start + SKU_CHUNK), includeEmpty: true },
      stillWanted,
    );
    if (page === null) {
      return null;
    }
    found.push(...page.filter((row) => missing.has(`${row.sku}@${row.locationCode}`)));
  }
  return found;
};

/** The catalog details for every sku, a chunk at a time and a few chunks at once, in the order the skus came. */
export const lookupProductsInChunks = async (
  lookup: (skus: string[]) => Promise<ProductLookupResponse[]>,
  skus: string[],
): Promise<ProductLookupResponse[]> => {
  const chunks: string[][] = [];
  for (let start = 0; start < skus.length; start += SKU_CHUNK) {
    chunks.push(skus.slice(start, start + SKU_CHUNK));
  }
  const answers: ProductLookupResponse[][] = [];
  for (let start = 0; start < chunks.length; start += CATALOG_LOOKUP_PARALLEL) {
    answers.push(...(await Promise.all(chunks.slice(start, start + CATALOG_LOOKUP_PARALLEL).map(lookup))));
  }
  return answers.flat();
};
